import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Link, useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { useContextoDeLista } from "@/features/assistente/contexto-de-tela";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  GitBranch,
  Mail,
  Plus,
  Search,
  Pencil,
  Trash2,
  Play,
  Pause,
  Zap,
  Clock,
  CheckCircle2,
  XCircle,
  Activity,
  MoreHorizontal,
  Copy,
  Eye,
  FileText,
  Users,
} from "lucide-react";

// ─── Execution status badge ──────────────────────────────────────────────────
const EXEC_STATUS_CONFIG: Record<string, { label: string; color: string; icon: any }> = {
  completed: { label: "Concluída", color: "text-green-600", icon: CheckCircle2 },
  failed: { label: "Falhou", color: "text-red-600", icon: XCircle },
  running: { label: "Em execução", color: "text-blue-600", icon: Activity },
};

function FlowExecutionBadge({ flowId }: { flowId: number }) {
  const { data: executions = [] } = trpc.flows.listExecutions.useQuery(
    { flowId, limit: 5 },
    { refetchInterval: 10000 }
  );
  if (executions.length === 0) return <span className="text-muted-foreground">—</span>;
  const last = executions[0] as any;
  const cfg = EXEC_STATUS_CONFIG[last.status] ?? { label: last.status, color: "text-gray-500", icon: Activity };
  const Icon = cfg.icon;
  return (
    <div className="flex items-center gap-1.5 text-xs">
      <Icon className={`w-3.5 h-3.5 ${cfg.color}`} />
      <span className={cfg.color}>{cfg.label}</span>
      <span className="text-muted-foreground">({executions.length})</span>
    </div>
  );
}

// ─── Constants ───────────────────────────────────────────────────────────────
// Gatilhos sensatos para e-mail. WhatsApp tem "keyword/any_message" — aqui não.
const TRIGGER_LABELS: Record<string, string> = {
  manual: "Manual / Campanha",
  tag_added: "Tag adicionada",
  new_contact: "Novo contato",
  webhook: "Webhook (integração externa)",
  scheduled: "Agendado (data específica)",
  // Legados (fluxos antigos podem ter sido criados como keyword antes do fix)
  keyword: "Palavra-chave (legado)",
  any_message: "Qualquer mensagem (legado)",
  opt_in: "Opt-in (legado)",
};

// Descrições mostradas dentro do select pra contexto.
const TRIGGER_DESCRIPTIONS: Record<string, string> = {
  manual: "Você dispara o fluxo a partir do CRM, de uma campanha ou manualmente.",
  tag_added: "Roda automaticamente quando a tag escolhida é aplicada a um contato.",
  new_contact: "Roda quando um novo contato é criado no tenant.",
  webhook: "Roda quando uma integração externa chama o endpoint do fluxo.",
  scheduled: "Dispara uma vez na data/hora configurada (campanha pontual).",
};

const STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "outline" | "destructive"; className: string }> = {
  active: { label: "Ativo", variant: "default", className: "bg-green-100 text-green-800 border-green-200 hover:bg-green-100" },
  inactive: { label: "Inativo", variant: "outline", className: "bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-100" },
  draft: { label: "Rascunho", variant: "outline", className: "bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-100" },
};

type StatusFilter = "all" | "active" | "inactive" | "draft";

export default function Flows() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
    triggerType: "manual",
    triggerValue: "",
  });

  const utils = trpc.useUtils();
  const { data: flows = [], isLoading } = trpc.flows.list.useQuery({ channel: 'email' });

  useContextoDeLista({
    titulo: "Fluxos de e-mail",
    itens: (flows ?? []).map((f: any) => ({ id: f.id, nome: f.name, status: f.status, gatilho: f.triggerType })),
  });

  const [, navigate] = useLocation();

  const createMutation = trpc.flows.create.useMutation({
    onSuccess: (created) => {
      utils.flows.list.invalidate();
      setCreateOpen(false);
      setForm({ name: "", description: "", triggerType: "manual", triggerValue: "" });
      if (created?.id) navigate(`/flows/${created.id}`);
    },
  });
  const updateMutation = trpc.flows.update.useMutation({
    onSuccess: () => utils.flows.list.invalidate(),
  });
  const deleteMutation = trpc.flows.delete.useMutation({
    onSuccess: () => { utils.flows.list.invalidate(); setDeleteId(null); },
  });

  // Filters
  const filtered = flows.filter(f => {
    const matchesSearch = f.name.toLowerCase().includes(search.toLowerCase()) ||
      (f.description ?? "").toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || f.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const toggleStatus = (flow: typeof flows[0]) => {
    const next = flow.status === "active" ? "inactive" : "active";
    updateMutation.mutate({ id: flow.id, status: next as any });
  };

  // Stats
  const stats = {
    total: flows.length,
    active: flows.filter(f => f.status === "active").length,
    draft: flows.filter(f => f.status === "draft").length,
    inactive: flows.filter(f => f.status === "inactive").length,
  };

  return (
    <DashboardLayout>
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Mail className="w-6 h-6 text-primary" />
            Fluxos de E-mail
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Crie e gerencie fluxos de automação de e-mail marketing
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" disabled className="gap-2">
            <FileText className="w-4 h-4" />
            Usar um Modelo
          </Button>
          <Button onClick={() => setCreateOpen(true)} className="gap-2">
            <Plus className="w-4 h-4" />
            Criar Fluxo
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {([
          { label: "Total de Fluxos", value: stats.total, icon: GitBranch, color: "text-blue-500", filter: "all" as StatusFilter },
          { label: "Ativos", value: stats.active, icon: Play, color: "text-green-500", filter: "active" as StatusFilter },
          { label: "Rascunhos", value: stats.draft, icon: Pencil, color: "text-yellow-500", filter: "draft" as StatusFilter },
          { label: "Inativos", value: stats.inactive, icon: Pause, color: "text-gray-400", filter: "inactive" as StatusFilter },
        ]).map(stat => (
          <Card
            key={stat.label}
            className={`border-border/50 cursor-pointer transition-all hover:border-primary/30 ${statusFilter === stat.filter ? "ring-2 ring-primary/20 border-primary/40" : ""}`}
            onClick={() => setStatusFilter(stat.filter)}
          >
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <stat.icon className={`w-8 h-8 ${stat.color}`} />
                <div>
                  <p className="text-2xl font-bold">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search + Status Filter */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar fluxos..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Filtrar por status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="active">Ativos</SelectItem>
            <SelectItem value="draft">Rascunhos</SelectItem>
            <SelectItem value="inactive">Inativos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {isLoading ? (
        <Card>
          <CardContent className="py-12">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        /* Empty State */
        <Card className="border-dashed border-2 border-border/50">
          <CardContent className="flex flex-col items-center justify-center py-20 gap-5">
            <div className="h-20 w-20 rounded-full bg-muted flex items-center justify-center">
              <Mail className="w-10 h-10 text-muted-foreground/40" />
            </div>
            <div className="text-center max-w-md">
              <p className="font-semibold text-lg text-foreground">
                {search || statusFilter !== "all" ? "Nenhum fluxo encontrado" : "Crie seu primeiro fluxo de automação"}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                {search || statusFilter !== "all"
                  ? "Tente ajustar os filtros ou termo de busca"
                  : "Automatize respostas, engaje leads e crie jornadas personalizadas para seus contatos."
                }
              </p>
            </div>
            {!search && statusFilter === "all" && (
              <div className="flex gap-3">
                <Button onClick={() => setCreateOpen(true)} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Criar Fluxo
                </Button>
                <Button variant="outline" disabled className="gap-2">
                  <FileText className="w-4 h-4" />
                  Usar um Modelo
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[280px]">Nome</TableHead>
                <TableHead className="w-[120px]">Status</TableHead>
                <TableHead>Gatilho</TableHead>
                <TableHead>Data de criação</TableHead>
                <TableHead>Última execução</TableHead>
                <TableHead className="w-[60px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(flow => {
                const statusCfg = STATUS_CONFIG[flow.status ?? "draft"];
                return (
                  <TableRow key={flow.id} className="group cursor-pointer" onClick={() => navigate(`/flows/${flow.id}`)}>
                    <TableCell>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{flow.name}</p>
                        {flow.description && (
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{flow.description}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-xs ${statusCfg.className}`}>
                        {statusCfg.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-sm">
                        <Zap className="w-3.5 h-3.5 text-yellow-500 shrink-0" />
                        <span>{TRIGGER_LABELS[flow.triggerType ?? "keyword"] ?? flow.triggerType}</span>
                        {flow.triggerValue && (
                          <span className="bg-muted px-1.5 py-0.5 rounded font-mono text-xs">
                            "{flow.triggerValue}"
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <Clock className="w-3.5 h-3.5" />
                        {new Date(flow.createdAt!).toLocaleDateString("pt-BR")}
                      </div>
                    </TableCell>
                    <TableCell>
                      <FlowExecutionBadge flowId={flow.id} />
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Mais opções">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenuItem onClick={() => navigate(`/flows/${flow.id}`)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/flows/${flow.id}/executions`)}>
                            <Users className="w-4 h-4 mr-2" />
                            Ver execuções
                          </DropdownMenuItem>
                          <DropdownMenuItem disabled>
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleStatus(flow)}>
                            {flow.status === "active" ? (
                              <>
                                <Pause className="w-4 h-4 mr-2" />
                                Desativar
                              </>
                            ) : (
                              <>
                                <Play className="w-4 h-4 mr-2" />
                                Ativar
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem disabled>
                            <Eye className="w-4 h-4 mr-2" />
                            Preview
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => setDeleteId(flow.id)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="w-[95vw] max-w-md mx-2 sm:mx-0">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              Novo Fluxo de E-mail
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label>Nome do Fluxo *</Label>
              <Input
                placeholder="Ex: Boas-vindas, Suporte, Vendas..."
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Descrição</Label>
              <Textarea
                placeholder="Descreva o objetivo deste fluxo..."
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={2}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Como esse fluxo dispara?</Label>
              <Select value={form.triggerType} onValueChange={v => setForm(f => ({ ...f, triggerType: v, triggerValue: "" }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual / Campanha</SelectItem>
                  <SelectItem value="tag_added">Tag adicionada</SelectItem>
                  <SelectItem value="new_contact">Novo contato</SelectItem>
                  <SelectItem value="webhook">Webhook (integração externa)</SelectItem>
                  <SelectItem value="scheduled">Agendado (data específica)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {TRIGGER_DESCRIPTIONS[form.triggerType] ?? ""}
              </p>
            </div>

            {form.triggerType === "tag_added" && (
              <div className="flex flex-col gap-1.5">
                <Label>Nome da tag</Label>
                <Input
                  placeholder="Ex: lead-quente, baixou-ebook"
                  value={form.triggerValue}
                  onChange={e => setForm(f => ({ ...f, triggerValue: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">O fluxo dispara quando esta tag é aplicada ao contato.</p>
              </div>
            )}

            {form.triggerType === "scheduled" && (
              <div className="flex flex-col gap-1.5">
                <Label>Data e hora</Label>
                <Input
                  type="datetime-local"
                  value={form.triggerValue}
                  onChange={e => setForm(f => ({ ...f, triggerValue: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">
                  Pode ser editado depois nas configurações do fluxo.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button
              onClick={() => createMutation.mutate({ ...form, channel: 'email' })}
              disabled={!form.name.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? "Criando..." : "Criar e Editar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Fluxo</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O fluxo e todas as suas configurações serão removidos permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteId && deleteMutation.mutate({ id: deleteId })}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </DashboardLayout>
  );
}
